import { type Editor } from '@tiptap/core';
import { useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  docToBlocks,
  docToMarkdown,
  hasMarkdownDrift,
  markdownToDoc,
} from '@/shared/utils/markdown-doc/markdown-doc.util';

import { MARKDOWN_PROSE_CLASSES } from '../structured.tokens';
import { type MarkdownHeadingLevel, type MarkdownToolbarState } from '../MarkdownToolbar';
import { useStructuredAnnouncer } from '../useStructuredAnnouncer/useStructuredAnnouncer';

interface UseMarkdownEditorLogicArgs {
  value: string;
  onValueChange: (value: string) => void;
  onBlur?: () => void;
  dir: 'ltr' | 'rtl';
  fieldId: string;
  label: string;
  describedBy?: string;
  isDisabled: boolean;
  isReadOnly: boolean;
  isInvalid: boolean;
}

export interface UseMarkdownEditorLogic {
  editor: Editor;
  toolbar: MarkdownToolbarState;
  hasDrift: boolean;
  announcement: string;
  focus: () => void;
  toggleHeading: (level: MarkdownHeadingLevel) => void;
  toggleBold: () => void;
  toggleBulletList: () => void;
  toggleOrderedList: () => void;
  undo: () => void;
  redo: () => void;
}

const EMPTY_TOOLBAR: MarkdownToolbarState = {
  headingLevel: null,
  isBold: false,
  isBulletList: false,
  isOrderedList: false,
  canUndo: false,
  canRedo: false,
};

/**
 * Only what the legal grammar can carry. A format that is not in this list is
 * not a missing feature: it is text the member's app would show raw.
 */
const extensions = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    italic: false,
    strike: false,
    code: false,
    codeBlock: false,
    blockquote: false,
    horizontalRule: false,
    // A hard-wrapped line is joined on read, so a soft break would vanish.
    hardBreak: false,
    link: false,
    underline: false,
    dropcursor: false,
    gapcursor: false,
    trailingNode: false,
  }),
];

export function useMarkdownEditorLogic({
  value,
  onValueChange,
  onBlur,
  dir,
  fieldId,
  label,
  describedBy,
  isDisabled,
  isReadOnly,
  isInvalid,
}: UseMarkdownEditorLogicArgs): UseMarkdownEditorLogic {
  const announcer = useStructuredAnnouncer();
  const [hasDrift, setHasDrift] = useState(false);

  // The markdown this editor last handed out. An incoming `value` equal to it
  // is our own echo through the form and must not be written back — that is
  // the whole defence against the loop, and what keeps `isDirty` honest.
  const lastEmitted = useRef(value);
  // The editor's handlers are bound once; these refs hand them the latest props.
  const callbacks = useRef({ onValueChange, onBlur });
  const readOnly = useRef(isReadOnly);

  useEffect(() => {
    callbacks.current = { onValueChange, onBlur };
    readOnly.current = isReadOnly;
  }, [onValueChange, onBlur, isReadOnly]);

  const editable = !isDisabled && !isReadOnly;

  // `setOptions` replaces `editorProps` wholesale, so the key handler must
  // travel with the attributes every time — built in one place for both.
  const buildEditorProps = useCallback(
    () => ({
      attributes: editorAttributes({ fieldId, label, describedBy, dir, isInvalid, isReadOnly }),
      handleKeyDown: () => {
        if (readOnly.current) announcer.announceReadOnlyRefusal();
        return false;
      },
    }),
    [fieldId, label, describedBy, dir, isInvalid, isReadOnly, announcer],
  );

  const editor = useEditor({
    extensions,
    content: markdownToDoc(value),
    editable,
    editorProps: buildEditorProps(),
    onUpdate: ({ editor: instance }) => {
      const json = instance.getJSON();
      const blocks = docToBlocks(json);
      const markdown = docToMarkdown(json);

      setHasDrift(hasMarkdownDrift(blocks));
      lastEmitted.current = markdown;
      callbacks.current.onValueChange(markdown);
    },
    onBlur: () => {
      callbacks.current.onBlur?.();
    },
  });

  // An external value — a `reset()`, an async prefill — replaces the document
  // without emitting, so the form does not see its own load as an edit.
  useEffect(() => {
    if (value === lastEmitted.current) return;

    lastEmitted.current = value;
    editor.commands.setContent(markdownToDoc(value), { emitUpdate: false });
    setHasDrift(false);
  }, [editor, value]);

  useEffect(() => {
    editor.setEditable(editable, false);
  }, [editor, editable]);

  // The attributes travel through `setOptions` rather than the editor's deps:
  // rebuilding the editor on every validation toggle would drop the selection.
  useEffect(() => {
    editor.setOptions({ editorProps: buildEditorProps() });
  }, [editor, buildEditorProps]);

  // Read through a selector: TipTap does not re-render on every transaction,
  // so `aria-pressed` on the toolbar would otherwise freeze at mount.
  const toolbar =
    useEditorState({
      editor,
      selector: ({ editor: instance }): MarkdownToolbarState => ({
        headingLevel:
          ([1, 2, 3] as const).find((level) => instance.isActive('heading', { level })) ?? null,
        isBold: instance.isActive('bold'),
        isBulletList: instance.isActive('bulletList'),
        isOrderedList: instance.isActive('orderedList'),
        canUndo: instance.can().undo(),
        canRedo: instance.can().redo(),
      }),
    }) ?? EMPTY_TOOLBAR;

  const focus = useCallback(() => {
    editor.commands.focus();
  }, [editor]);

  const toggleHeading = useCallback(
    (level: MarkdownHeadingLevel) => {
      editor.chain().focus().toggleHeading({ level }).run();
    },
    [editor],
  );

  const toggleBold = useCallback(() => {
    editor.chain().focus().toggleBold().run();
  }, [editor]);

  const toggleBulletList = useCallback(() => {
    editor.chain().focus().toggleBulletList().run();
  }, [editor]);

  const toggleOrderedList = useCallback(() => {
    editor.chain().focus().toggleOrderedList().run();
  }, [editor]);

  const undo = useCallback(() => {
    editor.chain().focus().undo().run();
  }, [editor]);

  const redo = useCallback(() => {
    editor.chain().focus().redo().run();
  }, [editor]);

  return {
    editor,
    toolbar,
    hasDrift,
    announcement: announcer.announcement,
    focus,
    toggleHeading,
    toggleBold,
    toggleBulletList,
    toggleOrderedList,
    undo,
    redo,
  };
}

/**
 * The contenteditable's own ARIA. It is the control, so the name, the
 * description and the validity all sit on it — not on a wrapper a screen reader
 * never lands on.
 */
function editorAttributes({
  fieldId,
  label,
  describedBy,
  dir,
  isInvalid,
  isReadOnly,
}: {
  fieldId: string;
  label: string;
  describedBy: string | undefined;
  dir: 'ltr' | 'rtl';
  isInvalid: boolean;
  isReadOnly: boolean;
}): Record<string, string> {
  return {
    id: fieldId,
    role: 'textbox',
    'aria-multiline': 'true',
    'aria-label': label,
    ...(describedBy === undefined ? {} : { 'aria-describedby': describedBy }),
    ...(isInvalid ? { 'aria-invalid': 'true' } : {}),
    ...(isReadOnly ? { 'aria-readonly': 'true' } : {}),
    dir,
    class: MARKDOWN_PROSE_CLASSES,
  };
}
